import React from "react";
import * as utils from "common/utils";
import SummaryRow from "./SummaryRow";
import Alert from "components/Alert";
import FileSummary from "./FileSummary";
import Watermark from "components/Watermark";
import NutrientBudgetWidget from "../ResultsWidgets/NutrientBudgetWidget";
import { Bar } from "react-chartjs-2";

const hasOverview = (budget = {}) => {
    const { currentResults = {} } = budget;
    const { nitrogenOverview, phosphorusOverview } = currentResults;
    return nitrogenOverview || phosphorusOverview;
};

export const NutrientSummary = ({ before, after }) => {
    const CalcSummaryValues = ({ srcKey, render, parent }) => {
        const actualValue = srcKey ? after.details && after.details.currentResults && after.details.currentResults[parent] && after.details.currentResults[parent][srcKey] : after.details.currentResults && after.details.currentResults[parent];
        const baseValue = srcKey ? before && before.details && before.details.currentResults && before.details.currentResults[parent] && before.details.currentResults[parent][srcKey] : before && before.details.currentResults && before.details.currentResults[parent];
        return render(before ? baseValue || 0 : baseValue, actualValue);
    };

    if (!hasOverview(after.details) && (!before || !hasOverview(before.details))) return null;

    return (
        <div className="ReportSection">
            <h1>Nutrients</h1>
            <div className="Table Table-Compressed">
                <Watermark />
                <table>
                    <thead>
                        <tr>
                            <th data-width="25"></th>
                            <th></th>
                            {before && (
                                <th className="u-textCenter u-before" data-width="12.5">
                                    {utils.truncateText(before.displayName, 35)}
                                </th>
                            )}
                            <th className={before ? "u-after u-textCenter" : "u-textCenter"} data-width="12.5">
                                {utils.truncateText(after.displayName, 35)}
                            </th>
                            {before && (
                                <th className="u-textCenter" data-width="16.7">
                                    Change
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        <CalcSummaryValues srcKey="farmTotalLoss" parent="nitrogenOverview" render={(baseValue, actualValue) => <SummaryRow rowClass="Table-tr--nloss Table-tr--start" category="Nitrogen" prefix="Nitrogen" baseValue={baseValue} actualValue={actualValue} label="Total loss" unit="kg" />} />
                        <CalcSummaryValues srcKey="farmLossPerHa" parent="nitrogenOverview" render={(baseValue, actualValue) => <SummaryRow rowClass="Table-tr--nloss Table-tr--middle" category=" " prefix="Nitrogen" baseValue={baseValue} actualValue={actualValue} label="Loss/ha" unit="kg/ha" />} />
                        <CalcSummaryValues parent="nce" render={(baseValue, actualValue) => <SummaryRow rowClass="Table-tr--nloss Table-tr--middle" category=" " prefix="Nitrogen" label="NCE" baseValue={baseValue} actualValue={actualValue} unit="%" />} />
                        <CalcSummaryValues parent="nSurplus" render={(baseValue, actualValue) => <SummaryRow rowClass="Table-tr--nloss Table-tr--end" category=" " prefix="Nitrogen" label="N Surplus" baseValue={baseValue} actualValue={actualValue} unit="kg/ha" />} />
                        <CalcSummaryValues srcKey="farmTotalLoss" parent="phosphorusOverview" render={(baseValue, actualValue) => <SummaryRow rowClass="Table-tr--ploss Table-tr--start" category="Phosphorus" prefix="Phosphorus" baseValue={baseValue} actualValue={actualValue} label="Total loss" unit="kg" />} />
                        <CalcSummaryValues srcKey="farmLossPerHa" parent="phosphorusOverview" render={(baseValue, actualValue) => <SummaryRow rowClass="Table-tr--ploss Table-tr--end" category=" " prefix="Phosphorus" baseValue={baseValue} actualValue={actualValue} label="Loss/ha" unit="kg/ha" />} />
                        <CalcSummaryValues parent="pSurplus" render={(baseValue, actualValue) => <SummaryRow rowClass="Table-tr--ploss Table-tr--end" category=" " prefix="Phosphorus" label="P Surplus" baseValue={baseValue} actualValue={actualValue} unit="kg/ha" />} />
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const NutrientExtraSummary = ({ before, after, refData }) => {
    const nutrientList = [
        { value: "N", text: "Nitrogen" },
        { value: "P", text: "Phosphorus" },
    ];
    return nutrientList.map((n, i) => {
        return (
            <div key={"nutrient-budget-" + i} className="ReportSection">
                <NutrientBudgetWidget refData={refData} before={before} after={after} nutrient={n} />
                <NutrientBalanceSheet before={before} after={after} refData={refData} nutrient={n} />
            </div>
        );
    });
};

const NutrientBalanceSheet = ({ before, after, refData, nutrient }) => {
    const nutrientSectionValues = (nutrientBudget, effect, block) => {
        const { nutrientBudgetCategoryTypes: categoryRefData = [] } = refData;

        return Object.keys(nutrientBudget.nutrientCategories)
            .filter((c) => nutrientBudget.nutrientCategories[c] && nutrientBudget.nutrientCategories[c].total && nutrientBudget.nutrientCategories[c].effect === effect && (c === "Effluent" && effect === "Added" ? block !== undefined : true))
            .filter((c) => effect !== "Added" || block === undefined || block.type !== "ProductiveOutdoorPigs" || !["Effluent", "Fertiliser", "Irrigation"].includes(c))
            .map((c) => {
                const label = categoryRefData.find((r) => r.value === c) || { text: "Unknown" };
                var labelText = label.text;
                if (block !== undefined) {
                    if (block.type === "ProductiveFruit" || block.type === "FodderCrop") {
                        if (label.value === "Supplements" && block.type === "ProductiveFruit") {
                            labelText = "Supplements for animals";
                        }
                        if (label.value === "InOrganicSoilPool") {
                            labelText = "Inorganic plant available";
                        }
                    }
                    if (block.type === "ProductivePasture") {
                        if (label.value === "Supplements") {
                            labelText = "Supplements fed on blocks";
                        }
                        // Cut  & Carry block only
                        if (block.animals && block.animals.length <= 0) {
                            if (label.value === "SupplementsAndResidues") {
                                labelText = "As Supplements";
                            }
                        }
                    }
                }
                const adjPool = (nutCat) => {
                    const result = {
                        N: 0,
                        P: 0,
                        K: 0,
                        S: 0,
                    };
                    Object.keys(nutCat.subTotals).forEach((key) => {
                        const sign = ["Mineralisation", "SlowRelease"].includes(key) ? 1 : -1;
                        result.N += (nutCat.subTotals[key].nutrients.N || 0) * sign;
                        result.P += (nutCat.subTotals[key].nutrients.P || 0) * sign;
                        result.K += (nutCat.subTotals[key].nutrients.K || 0) * sign;
                        result.S += (nutCat.subTotals[key].nutrients.S || 0) * sign;
                    });

                    return nutCat.total.nutrients;
                };
                const nutrients = effect === "ChangeInPools" ? adjPool(nutrientBudget.nutrientCategories[c]) : nutrientBudget.nutrientCategories[c].total.nutrients;

                return { labelText, nutrients, name: c };
            });
    };

    const extractValue = (budget) => {
        const nutrientBudget = budget && budget.currentResults && budget.currentResults.nutrientBudget;
        if (!nutrientBudget) return;

        const addedData = nutrientBudget && nutrientSectionValues(nutrientBudget, "Added");
        const removedData = nutrientBudget && nutrientSectionValues(nutrientBudget, "Removed");
        const changeInPools = nutrientBudget && nutrientSectionValues(nutrientBudget, "ChangeInPools");
        return { addedData, removedData, changeInPools };
    };

    const actualValue = extractValue(after.details);
    const baseValue = extractValue(before && before.details);

    if (!actualValue) return null;

    const actualAddedNames = actualValue.addedData.map((ad) => ad.labelText);
    const baseAddedNames = baseValue && baseValue.addedData.map((ad) => ad.labelText);
    const uniqueAddedNames = Array.from(new Set([...(baseAddedNames || []), ...actualAddedNames]));

    const actualRemovedNames = actualValue.removedData.map((ad) => ad.labelText);
    const baseRemovedNames = baseValue && baseValue.removedData.map((ad) => ad.labelText);
    const uniqueRemovedNames = Array.from(new Set([...actualRemovedNames, ...(baseRemovedNames || [])]));

    const actualChangeNames = actualValue.changeInPools.map((ad) => ad.labelText);
    const baseChangeNames = baseValue && baseValue.changeInPools.map((ad) => ad.labelText);
    const uniqueChangeNames = Array.from(new Set([...actualChangeNames, ...(baseChangeNames || [])]));

    const allLabels = [...uniqueAddedNames, ...uniqueRemovedNames, ...uniqueChangeNames];

    const getDataset = (value) => {
        const result = [];
        if (value) {
            uniqueAddedNames.forEach((n) => {
                const val = value.addedData.find((v) => v.labelText === n);
                result.push(val ? val.nutrients[nutrient.value] : 0);
            });
            uniqueRemovedNames.forEach((n) => {
                const val = value.removedData.find((v) => v.labelText === n);
                result.push(val ? val.nutrients[nutrient.value] * -1 : 0);
            });
            uniqueChangeNames.forEach((n) => {
                const val = value.changeInPools.find((v) => v.labelText === n);
                result.push(val ? val.nutrients[nutrient.value] : 0);
            });
        }
        return result;
    };

    const barOptions = {
        scales: {
            x: {
                display: true,
                grid: { zeroLineWidth: 2 },
            },
            y: {
                display: true,
                title: { display: true, text: "kg/ha/yr" },
            },
        },
        maintainAspectRatio: false,
        responsive: true,
    };

    const colours = [...uniqueAddedNames.map((n) => utils.graphColoursMuted[1]), ...uniqueRemovedNames.map((n) => utils.graphColoursMuted[2]), ...uniqueChangeNames.map((n) => utils.graphColoursMuted[4])];

    const barData = {
        labels: allLabels,
        datasets: [
            {
                label: after.displayName,
                data: getDataset(actualValue),
                backgroundColor: colours,
                borderWidth: 1,
            },
        ],
    };

    if (before) {
        barData.datasets.push({
            label: before.displayName,
            data: getDataset(baseValue),
            backgroundColor: "#E3EEF8",
            borderColor: colours,
            borderWidth: 1,
        });
    }

    return (
        <div className="ReportSection u-page-break-avoid">
            <h2>{nutrient.text} Movements</h2>

            <div className="printable-chart-container">
                <Bar data={barData} options={barOptions} className="printable-chart" />
            </div>

            <div className="u-flex u-flexJustifyCenter">
                <div className="u-pr-lg u-flex">
                    <div style={{ backgroundColor: utils.graphColoursMuted[1], width: "30px", height: "20px", marginRight: "6px" }}></div>
                    <span>Nutrients added</span>
                </div>
                <div className="u-pr-lg u-flex">
                    <div style={{ backgroundColor: utils.graphColoursMuted[2], width: "30px", height: "20px", marginRight: "6px" }}></div>
                    <span>Nutrients removed</span>
                </div>
                <div className="u-flex">
                    <div style={{ backgroundColor: utils.graphColoursMuted[4], width: "30px", height: "20px", marginRight: "6px" }}></div>
                    <span>Change in pools</span>
                </div>
            </div>
        </div>
    );
};

const Nutrients = ({ before, after, refData, showFileComparison = true }) => {
    const scenarios = [after];
    if (before) scenarios.unshift(before);

    if (!hasOverview(after.details) && (!before || !hasOverview(before.details))) {
        return (
            <div className="u-pt-lg">
                <Alert text="The selected analysis does not contain nutrient information" type="Info" />
            </div>
        );
    }

    return (
        <div className="ReportPage">
            {showFileComparison && before && <FileSummary before={before} after={after} mini={true} />}
            <NutrientSummary before={before} after={after} refData={refData} />
            <NutrientExtraSummary before={before} after={after} refData={refData} />
        </div>
    );
};

export default Nutrients;
